import fp from 'lodash/fp';

/**
 * Validates an email address.
 *
 * Returns true if the provided email address is valid, otherwise false.
 */
function validateEmail(email) {
  // eslint-disable-next-line
  const re = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
}

/**
 * Validates a password.
 *
 * Returns true if the provided password is valid, otherwise false.
 */
function validatePassword(password) {
  return password.trim() !== '';
}

function focusElement(elemToFocus) {
  elemToFocus.focus();

  // https://caniuse.com/#feat=scrollintoview looks pretty good but just in case.
  if (elemToFocus.scrollIntoView) {
    elemToFocus.scrollIntoView();
  }

  // Just in case we ever implement SSR.
  if (window) {
    // HACK: Account for fixed header height in our design.
    // The header height changes based on breakpoint so let's calculate it dynamically.
    const nav = window.document.querySelector('nav');
    const navHeight = nav ? nav.offsetHeight + 50 : 0;
    window.scroll(0, window.scrollY - navHeight);
  }
}

/**
 * Handles an invalid form. Scrolls into view and sets focus to the first invalid form element
 * within the form, or the form element itself if no invalid form element. Form elements are
 * considered invalid if they have an aria-invalid="true" attribute set.
 *
 * @see https://webaim.org/techniques/formvalidation/
 * @see https://reactjs.org/docs/refs-and-the-dom.html
 *
 * @param form The DOM reference to the invalid form that has just been validated.
 */
function handleInvalidForm(form) {
  // We use form.querySelector to find the first invalid element for three reasons:
  // 1) It means the component author only has to get a single React DOM ref (to the form itself).
  // 2) It means the component author doesn't have to work out which validation error is for
  //    the "first invalid element" (i.e. first in DOM order) and we'll always focus the first invalid
  //    element (which is important for usability and accessibility).
  // 3) It means the component author is forced to set aria-invalid correctly, because it's what we use to
  //    set focus and scroll into view.
  if (form && form.querySelector) {
    // HACK: We do this in a setTimeout so components can call it after they do validation but before
    // React has had a chance to re-render the component.
    setTimeout(() => {
      // Focus the first invalid form element, or, if no
      // aria-invalid form element, assume there is a global error message
      // at the top of the form and focus the form itself.
      const firstInvalidInput = form.querySelector('[aria-invalid=true]');

      focusElement(firstInvalidInput || form);
    });
  }
}

const validators = {
  required: (message) => (v) => (v !== undefined && v !== null) ? null : message,
  notEmpty: (message) => (v) => (v && v.length > 0) ? null : message,
  string: (message) => (v) => (typeof v) === "string" ? null : message,
  list: (message) => (v) => (typeof v) === "object" ? null : message,
  date: (message) => (v) => !isNaN(Date.parse(v)) ? null : message,
  past: (message) => (v) => Date.parse(v) < Date.parse(new Date()) ? null : message,
  future: (message) => (v) => Date.parse(v) > Date.parse(new Date()) ? null : message
};

const validatorSets = {
  nonEmptyString: [
    validators.string,
    validators.required,
    validators.notEmpty
  ],
  validDate: [
    validators.date
  ],
  pastDate: [
    validators.date,
    validators.past
  ],
  futureDate: [
    validators.date,
    validators.future
  ],
  nonEmptyList: [
    validators.list,
    validators.required,
    validators.notEmpty
  ]
}

const parsers = {
  string: (s) => s,
  integer: (s) => parseInt(s, 10),
  float: parseFloat
}

const validate = ({ validators, value }) => fp.flow(
  fp.map(
    validator => validator(value)
  ),
  fp.filter(
    validation => !fp.isEmpty(validation)
  )
)(validators)

const allValid = (items) => fp.flow(
  fp.map(
    validate
  ),
  fp.filter(
    validations => validations.length > 0
  )
)(items).length === 0

export { validateEmail, validatePassword, handleInvalidForm, focusElement, validators, validatorSets, parsers, allValid };

export const isNonEmptyString = (value) => (
  value !== undefined && value !== null && (typeof value) === 'string' && value !== ''
);

export const isNonEmptyNumber = (value) =>(
  value !== undefined && value !== null && (typeof value) === 'number'
);



// WEBPACK FOOTER //
// ./src/services/validator.js